import { ChartTooltipWrapperProps } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipWrapper';
import { TooltipPosition } from '@modules/charts/types';
import { useGetColor } from '@modules/common/hooks';
import { OTHERS_DATA_POINT_PIE_CHART } from '@modules/quantify/constants';
import { Grid, useControlled } from '@mui/material';
import { Box } from '@mui/system';
import { EqlField } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { useMeasure } from 'react-use';
import { Cell, Label, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { TimeRange } from 'v2/core/entities';
import { FieldNames } from 'v2/core/entities/fields';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { ChartTooltipWrapper } from '../ChartTooltipWrapper';
import { PieChartLabel, PieChartLabelProps } from './PieChartLabel';
import { PieChartLegend } from './PieChartLegend';

interface PieChartWithPaddingAngleWrapperProps {
  data: any[];
  dataKey: string;
  labelKey: string;
  label: PieChartLabelProps['label'];
  baseWhereClause: WhereClause;
  dateRange: TimeRange;
  fieldName?: FieldNames;
  qbField?: string;
  displayName?: string;
  minHeight?: number;
  shouldShowAddToFilter?: boolean;
  shouldDisableInteraction?: boolean;
  shouldShowLabel?: boolean;
  layout?: 'horizontal' | 'vertical';
  shouldShowLegendsInNewRow?: boolean;
  shouldDisableAnimation?: boolean;
  onMergeComplete?: () => void;
  formulaID: EQLFunctionName;
  refetch?: () => Promise<void[]>;
  measuredAsCriteria: EqlField | null;
  isSingleRecordQuery: boolean;
  shouldHideOpenInSearchOnFeedbackDrawer?: boolean;
}

export function PieChartWithPaddingAngleWrapper({
  data,
  dataKey,
  labelKey,
  label,
  baseWhereClause,
  dateRange,
  fieldName,
  qbField,
  displayName,
  minHeight = 270,
  shouldShowAddToFilter = true,
  shouldDisableInteraction = false,
  shouldShowLabel = true,
  layout,
  shouldShowLegendsInNewRow = false,
  shouldDisableAnimation = false,
  onMergeComplete,
  formulaID,
  refetch,
  measuredAsCriteria,
  isSingleRecordQuery,
  shouldHideOpenInSearchOnFeedbackDrawer = false,
}: PieChartWithPaddingAngleWrapperProps) {
  const getChartColor = useGetColor({ fieldName });
  const [tooltipData, setTooltipData] = useState<
    ChartTooltipWrapperProps['payload'] | null
  >(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] =
    useState<TooltipPosition | null>(null);
  const [pieChartRef, { width: containerWidth, height: containerHeight }] =
    useMeasure();

  const [chartLayout, setChartLayout] = useControlled({
    controlled: layout,
    default: undefined,
    name: 'PieChartWithPaddingAngleWrapper',
    state: 'layout',
  });

  useEffect(() => {
    if (!layout) {
      setChartLayout(containerWidth < 500 ? 'vertical' : 'horizontal');
    }
  }, [layout, setChartLayout, containerWidth]);

  const getGridBreakpoints = (gridGroup: 'pie' | 'legends') => {
    if (shouldShowLegendsInNewRow) {
      return 12;
    }

    if (chartLayout === 'horizontal' && !layout) {
      return 6;
    }

    if (chartLayout === 'horizontal') {
      return gridGroup === 'pie' ? 5 : 7;
    }

    return 12;
  };

  const handleMouseEnterOnLegend = (event: React.MouseEvent, index: number) => {
    if (
      shouldDisableInteraction ||
      data[index].id === OTHERS_DATA_POINT_PIE_CHART
    ) {
      return;
    }
    setShowTooltip(true);
    const dataItem = data[index];
    setTooltipData({
      id: dataItem.id,
      label: dataItem.label,
      value: dataItem.value ?? 0,
      category: dataItem.category,
    });
    setTooltipPosition({
      clientX: event.clientX,
      clientY: event.clientY,
    });
  };

  const handleMouseLeaveOnLegend = () => {
    if (shouldDisableInteraction) {
      return;
    }
    setShowTooltip(false);
  };

  const isSmallSize = containerWidth < 500;

  const pieGridBreakpoints = getGridBreakpoints('pie');
  const legendsGridBreakpoints = getGridBreakpoints('legends');

  const legendsLayout =
    chartLayout === 'horizontal' ? 'vertical' : 'horizontal';

  return (
    <Box
      ref={pieChartRef}
      width="100%"
      height="100%"
      data-role="pie-chart-wrapper"
      overflow={legendsLayout === 'vertical' ? 'hidden' : undefined}
    >
      {!shouldDisableInteraction && (
        <ChartTooltipWrapper
          payload={tooltipData}
          showTooltip={showTooltip}
          tooltipPosition={tooltipPosition}
          setShowTooltip={setShowTooltip}
          setTooltipPosition={setTooltipPosition}
          baseWhereClause={baseWhereClause}
          fieldName={fieldName}
          qbField={qbField}
          displayName={displayName}
          dateRange={dateRange}
          shouldShowAddToFilter={shouldShowAddToFilter}
          onMergeComplete={onMergeComplete}
          formulaID={formulaID}
          onFeedbackListDrawerClose={refetch}
          measuredAsCriteria={measuredAsCriteria}
          isSingleRecordQuery={isSingleRecordQuery}
          shouldHideOpenInSearchOnFeedbackDrawer={
            shouldHideOpenInSearchOnFeedbackDrawer
          }
        />
      )}
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        height="100%"
        rowGap={2}
      >
        <Grid
          item
          xs={pieGridBreakpoints}
          sx={{ '& .recharts-surface': { overflow: 'visible' } }}
          mt={legendsLayout === 'vertical' ? -2.5 : undefined}
        >
          <ResponsiveContainer minHeight={minHeight}>
            <PieChart>
              <Pie
                isAnimationActive={!shouldDisableAnimation}
                stroke="none"
                data={data}
                // This lets the pie auto scale based on available width & height
                innerRadius={isSmallSize ? '83%' : '65%'}
                outerRadius={isSmallSize ? '100%' : '80%'}
                paddingAngle={5}
                dataKey={dataKey}
                onMouseEnter={
                  shouldDisableInteraction
                    ? undefined
                    : (data, _, eve) => {
                        if (
                          data.tooltipPayload[0]?.payload.id ===
                          OTHERS_DATA_POINT_PIE_CHART
                        ) {
                          return;
                        }

                        setShowTooltip(true);
                        setTooltipData(data.tooltipPayload[0]?.payload);
                        setTooltipPosition({
                          clientX: eve.clientX,
                          clientY: eve.clientY,
                        });
                      }
                }
                onMouseLeave={
                  shouldDisableInteraction
                    ? undefined
                    : () => {
                        setShowTooltip(false);
                      }
                }
              >
                {data.map((item, index) => {
                  const label = item[labelKey];

                  return (
                    <Cell key={label} fill={getChartColor(index, label)} />
                  );
                })}
                {shouldShowLabel && (
                  <Label
                    position="center"
                    content={<PieChartLabel label={label} />}
                  />
                )}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </Grid>
        <Grid
          item
          xs={legendsGridBreakpoints}
          maxHeight={shouldShowLegendsInNewRow ? '100%' : containerHeight}
          overflow="auto"
          sx={
            shouldShowLegendsInNewRow
              ? {
                  margin: 2,
                }
              : null
          }
        >
          <PieChartLegend
            data={data}
            layout={legendsLayout}
            labelKey={labelKey}
            fieldName={fieldName}
            shouldShowLegendsInNewRow={shouldShowLegendsInNewRow}
            onMouseEnter={handleMouseEnterOnLegend}
            onMouseLeave={handleMouseLeaveOnLegend}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
